import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { usersService, type PickerUser } from '@/services/users.service';
import { cn } from '@/lib/utils';
import { initials, fullName } from './taskConstants';

interface AssigneePickerProps {
  selected: string[];
  onChange: (ids: string[]) => void;
  /** user ids that are already assigned and should be hidden/disabled. */
  excludeIds?: string[];
  /**
   * Extra classes for the scrolling list. Defaults to the `max-h-52` cap, which is what a
   * picker stacked in a single-column form wants. EditTaskModal's desktop layout overrides
   * it so the list flexes to the height of its column instead — see the note there on why
   * a fixed cap inside an already-scrolling parent produces two scrollbars.
   */
  listClassName?: string;
  /** The footer count. Off when the parent shows the tally in its own section header. */
  showSelectedCount?: boolean;
  /** Extra classes for the outer box, e.g. to let it flex inside a bounded column. */
  className?: string;
}

/** Multi-select user picker grouped by department, with search. */
export function AssigneePicker({
  selected,
  onChange,
  excludeIds = [],
  listClassName,
  showSelectedCount = true,
  className,
}: AssigneePickerProps) {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tasks', 'assignee-picker'],
    queryFn: () => usersService.picker(),
  });

  const grouped = useMemo(() => {
    const users = (data ?? []).filter((u) => !excludeIds.includes(u.id));
    const q = search.trim().toLowerCase();
    const filtered = q
      ? users.filter((u) =>
          fullName(u.first_name, u.last_name).toLowerCase().includes(q))
      : users;
    const map = new Map<string, PickerUser[]>();
    for (const u of filtered) {
      const dept = u.department_name ?? 'Unassigned';
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept)!.push(u);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data, search, excludeIds]);

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  return (
    <div className={cn(
      'flex min-h-0 flex-col overflow-hidden rounded-lg border border-border/60 bg-card',
      className,
    )}>
      <div className="shrink-0 border-b border-border/60 p-1.5">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search users…"
          containerClassName="w-full"
        />
      </div>
      {/* Height comes off the container, never off the rows below — those are py-2 with a
          28px avatar to stay on the 44px touch minimum. */}
      <div className={cn('thin-scrollbar min-h-0 flex-1 overflow-y-auto p-1', listClassName ?? 'max-h-52')}>
        {isLoading && (
          <div className="space-y-1.5 p-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-md bg-muted/60" />
            ))}
          </div>
        )}
        {isError && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Couldn’t load users.{' '}
            <button type="button" className="text-primary underline" onClick={() => refetch()}>Retry</button>
          </div>
        )}
        {!isLoading && !isError && grouped.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">No users found</div>
        )}
        {grouped.map(([dept, users]) => (
          <div key={dept} className="mb-0.5">
            {/* Sticky so the group a row belongs to stays readable while scrolling a long
                roster. z-[1] only has to beat its sibling rows, so it stays clear of the
                app's z-index token scale. */}
            <div className="sticky top-0 z-[1] bg-card/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
              {dept}
            </div>
            {users.map((u) => {
              const isSel = selected.includes(u.id);
              return (
                <button
                  type="button"
                  key={u.id}
                  onClick={() => toggle(u.id)}
                  className={cn(
                    // py-2 with a 28px avatar keeps the row on the 44px touch minimum;
                    // min-h-[44px] holds that floor even if a row ever loses its avatar.
                    'flex min-h-[44px] w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors',
                    isSel ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/50',
                  )}
                >
                  <span className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                    isSel ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                  )}>
                    {isSel && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-foreground">
                    {initials(u.first_name, u.last_name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {fullName(u.first_name, u.last_name)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {showSelectedCount && selected.length > 0 && (
        <div className="shrink-0 border-t border-border/60 px-2.5 py-1.5 text-[11px] text-muted-foreground">
          {selected.length} selected
        </div>
      )}
    </div>
  );
}
